import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { toast } from 'sonner';
import api, { advertisersAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatCurrency, formatNumber, getCurrencyList, getCurrencySymbol } from '../lib/utils';
import {
  ArrowLeft,
  Building2,
  Plus,
  Plug,
  Users,
  Trash2,
  Play,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Clock,
  Loader2,
  Edit,
  Save,
  Mail,
  User,
  Key,
  Eye,
  EyeOff,
  BarChart3,
  TrendingUp,
  Target,
  DollarSign,
  MousePointer,
  Zap,
  Pause,
  Megaphone,
  Bell,
  Search,
  ChevronDown,
  X,
  Camera,
  ImageIcon,
  Lock,
} from 'lucide-react';

const PLATFORMS = [
  { value: 'Meta', label: 'Meta (Facebook/Instagram)', icon: '📘' },
  { value: 'DV360', label: 'Google DV360', icon: '🎯' },
  { value: 'TikTok', label: 'TikTok Ads', icon: '🎵' },
  { value: 'LinkedIn', label: 'LinkedIn Ads', icon: '💼' },
  { value: 'Twitter', label: 'Twitter/X Ads', icon: '🐦' },
  { value: 'Mondiads', label: 'Mondiads', icon: '📊' },
  { value: 'Propeller', label: 'PropellerAds', icon: '🚀' },
  { value: 'Clickadu', label: 'Clickadu', icon: '🖱️' },
];

const getCredentialFields = (platform) => {
  switch (platform) {
    case 'Meta':
      return [
        { key: 'access_token', label: 'Access Token', type: 'password', required: true },
      ];
    case 'DV360':
      return [
        { key: 'client_id', label: 'Client ID', type: 'text', required: true },
        { key: 'client_secret', label: 'Client Secret', type: 'password', required: true },
        { key: 'refresh_token', label: 'Refresh Token', type: 'password', required: true },
        { key: 'partner_id', label: 'Partner ID', type: 'text', required: false },
      ];
    case 'TikTok':
      return [
        { key: 'app_id', label: 'App ID', type: 'text', required: true },
        { key: 'secret', label: 'App Secret', type: 'password', required: true },
        { key: 'access_token', label: 'Access Token', type: 'password', required: true },
      ];
    case 'LinkedIn':
      return [
        { key: 'access_token', label: 'Access Token', type: 'password', required: true },
      ];
    case 'Twitter':
      return [
        { key: 'api_key', label: 'API Key', type: 'text', required: true },
        { key: 'api_secret', label: 'API Secret', type: 'password', required: true },
        { key: 'access_token', label: 'Access Token', type: 'password', required: true },
        { key: 'access_secret', label: 'Access Token Secret', type: 'password', required: true },
      ];
    case 'Mondiads':
      return [
        { key: 'email', label: 'Email', type: 'email', required: true },
        { key: 'password', label: 'Password', type: 'password', required: true },
      ];
    case 'Propeller':
    case 'Clickadu':
      return [
        { key: 'api_token', label: 'API Token', type: 'password', required: true },
      ];
    default:
      return [];
  }
};

export const AdvertiserDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser, refreshAdvertiser } = useAuth();
  const isAdvertiserRole = currentUser?.role === 'advertiser';
  const isAdminRole = currentUser?.role === 'super_admin' || currentUser?.role === 'admin';
  const canEditProfile = isAdminRole || isAdvertiserRole; // Advertisers can edit their own profile
  
  const [advertiser, setAdvertiser] = useState(null);
  const [integrations, setIntegrations] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [logoEditing, setLogoEditing] = useState(false);
  const [newLogoUrl, setNewLogoUrl] = useState('');

  // Integration dialog
  const [integrationDialogOpen, setIntegrationDialogOpen] = useState(false);
  const [newIntegration, setNewIntegration] = useState({
    platform: '',
    account_name: '',
    credentials: {},
    user_id: '',  // For user-specific integration
  });
  const [creatingIntegration, setCreatingIntegration] = useState(false);
  const [showPasswords, setShowPasswords] = useState({});
  
  // Edit Integration state
  const [editIntegrationDialogOpen, setEditIntegrationDialogOpen] = useState(false);
  const [editingIntegration, setEditingIntegration] = useState(null);
  const [editIntegrationForm, setEditIntegrationForm] = useState({
    account_name: '',
    credentials: {},
    is_active: true,
  });
  const [savingIntegration, setSavingIntegration] = useState(false);
  const [editShowPasswords, setEditShowPasswords] = useState({});
  
  // Ad Accounts selection state (for Meta)
  const [adAccountsDialogOpen, setAdAccountsDialogOpen] = useState(false);
  const [adAccountsLoading, setAdAccountsLoading] = useState(false);
  const [adAccounts, setAdAccounts] = useState([]);
  const [selectedAdAccounts, setSelectedAdAccounts] = useState([]);
  const [savingAdAccounts, setSavingAdAccounts] = useState(false);
  const [adAccountsIntegration, setAdAccountsIntegration] = useState(null);
  const [adAccountLocked, setAdAccountLocked] = useState(false); // Whether ad account selection is locked

  // User dialog
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', name: '', password: '', role: 'user', allowed_platforms: null });
  const [creatingUser, setCreatingUser] = useState(false);
  const [userSelectAllPlatforms, setUserSelectAllPlatforms] = useState(true);
  
  // Edit user state
  const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editUserForm, setEditUserForm] = useState({ name: '', role: 'user', allowed_platforms: null });
  const [editUserSelectAllPlatforms, setEditUserSelectAllPlatforms] = useState(true);
  const [savingUser, setSavingUser] = useState(false);
  
  // Analytics state
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState('all');
  
  // Campaigns for alerts dropdown
  const [campaigns, setCampaigns] = useState([]);
  const [campaignSearch, setCampaignSearch] = useState('');
  const [campaignDropdownOpen, setCampaignDropdownOpen] = useState(false);
  
  // Alerts state
  const [alerts, setAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [alertDialogOpen, setAlertDialogOpen] = useState(false);
  const [newAlert, setNewAlert] = useState({
    name: '',
    campaign_id: '',
    alert_type: 'threshold',
    threshold_metric: 'spend',
    threshold_value: 0,
    channels: ['in_app'],
    is_active: true,
    cooldown_hours: 6,
  });
  const [creatingAlert, setCreatingAlert] = useState(false);

  const fetchAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      // Pass platforms filter for users with restricted access
      const params = {};
      if (currentUser?.allowed_platforms?.length > 0) {
        params.platforms = currentUser.allowed_platforms.join(',');
      }
      const response = await advertisersAPI.getAnalytics(id, params);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const fetchCampaignsForAlerts = async () => {
    try {
      const response = await api.get(`/advertisers/${id}/campaigns?page=1&page_size=100`);
      setCampaigns(response.data?.campaigns || []);
    } catch (error) {
      console.error('Failed to fetch campaigns for alerts:', error);
    }
  };

  const fetchAlerts = async () => {
    setAlertsLoading(true);
    try {
      const response = await api.get(`/advertisers/${id}/alerts`);
      setAlerts(response.data || []);
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
    } finally {
      setAlertsLoading(false);
    }
  };

  const handleCreateAlert = async () => {
    if (!newAlert.name) {
      toast.error('Alert name is required');
      return;
    }
    if (!newAlert.threshold_value || newAlert.threshold_value <= 0) {
      toast.error('Please enter a valid threshold value');
      return;
    }

    setCreatingAlert(true);
    try {
      await api.post(`/advertisers/${id}/alerts`, newAlert);
      toast.success('Alert created successfully');
      setAlertDialogOpen(false);
      setNewAlert({
        name: '',
        campaign_id: '',
        alert_type: 'threshold',
        threshold_metric: 'spend',
        threshold_value: 0,
        channels: ['in_app'],
        is_active: true,
        cooldown_hours: 6,
      });
      fetchAlerts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create alert');
    } finally {
      setCreatingAlert(false);
    }
  };

  const handleDeleteAlert = async (alertId) => {
    if (!window.confirm('Delete this alert?')) return;
    
    try {
      await api.delete(`/advertisers/${id}/alerts/${alertId}`);
      toast.success('Alert deleted');
      fetchAlerts();
    } catch (error) {
      toast.error('Failed to delete alert');
    }
  };

  const handleToggleAlert = async (alert) => {
    try {
      await api.put(`/advertisers/${id}/alerts/${alert.id}`, {
        is_active: !alert.is_active
      });
      toast.success(alert.is_active ? 'Alert paused' : 'Alert activated');
      fetchAlerts();
    } catch (error) {
      toast.error('Failed to update alert');
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      // Always fetch advertiser data
      const advResponse = await api.get(`/advertisers/${id}`);
      setAdvertiser(advResponse.data);
      setEditForm(advResponse.data);
      
      // Fetch integrations (may fail for user role - that's ok)
      try {
        const intResponse = await api.get(`/advertisers/${id}/integrations`);
        setIntegrations(intResponse.data);
      } catch (e) {
        console.log('Could not fetch integrations');
        setIntegrations([]);
      }
      
      // Fetch users (may fail for user role - that's ok)
      try {
        const usersResponse = await api.get(`/advertisers/${id}/users`);
        setUsers(usersResponse.data);
      } catch (e) {
        console.log('Could not fetch users');
        setUsers([]);
      }
    } catch (error) {
      toast.error('Failed to fetch advertiser data');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchAnalytics();
    fetchAlerts();
    fetchCampaignsForAlerts();
  }, [id, currentUser?.allowed_platforms]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/advertisers/${id}`, {
        name: editForm.name,
        contact_name: editForm.contact_name,
        contact_email: editForm.contact_email,
        logo_url: editForm.logo_url,
        notes: editForm.notes,
      });
      setAdvertiser(editForm);
      setEditing(false);
      toast.success('Advertiser updated');
      // Refresh advertiser in auth context to update sidebar logo
      if (refreshAdvertiser) {
        await refreshAdvertiser();
      }
    } catch (error) {
      toast.error('Failed to update advertiser');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveLogo = async () => {
    setSaving(true);
    try {
      await api.put(`/advertisers/${id}`, {
        logo_url: newLogoUrl,
      });
      setAdvertiser(prev => ({ ...prev, logo_url: newLogoUrl }));
      setLogoEditing(false);
      setNewLogoUrl('');
      toast.success('Logo updated successfully');
      // Refresh advertiser in auth context to update sidebar logo
      if (refreshAdvertiser) {
        await refreshAdvertiser();
      }
    } catch (error) {
      toast.error('Failed to update logo');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateIntegration = async () => {
    if (!newIntegration.platform || !newIntegration.account_name) {
      toast.error('Platform and account name are required');
      return;
    }

    setCreatingIntegration(true);
    try {
      const payload = {
        ...newIntegration,
        user_id: newIntegration.user_id || null,  // null means shared integration
      };
      await api.post(`/advertisers/${id}/integrations`, payload);
      toast.success('Integration added');
      setIntegrationDialogOpen(false);
      setNewIntegration({ platform: '', account_name: '', credentials: {}, user_id: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add integration');
    } finally {
      setCreatingIntegration(false);
    }
  };

  const handleTestIntegration = async (integrationId) => {
    try {
      const response = await api.post(`/advertisers/${id}/integrations/${integrationId}/test`);
      const result = response.data;
      if (result.status === 'connected') {
        toast.success(result.message);
      } else {
        toast.error(result.message);
      }
      fetchData();
    } catch (error) {
      toast.error('Connection test failed');
    }
  };

  const handleSyncIntegration = async (integrationId) => {
    try {
      await api.post(`/advertisers/${id}/integrations/${integrationId}/sync`);
      toast.success('Sync started in background');
      fetchData();
    } catch (error) {
      toast.error('Failed to start sync');
    }
  };

  const handleDeleteIntegration = async (integrationId) => {
    if (!window.confirm('Delete this integration? All associated data will be removed.')) {
      return;
    }

    try {
      await api.delete(`/advertisers/${id}/integrations/${integrationId}`);
      toast.success('Integration deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete integration');
    }
  };

  const handleEditIntegration = (integration) => {
    setEditingIntegration(integration);
    setEditIntegrationForm({
      account_name: integration.account_name || '',
      credentials: {},  // Don't pre-fill credentials for security
      is_active: integration.is_active !== false,
    });
    setEditShowPasswords({});
    setEditIntegrationDialogOpen(true);
  };

  const handleSaveIntegration = async () => {
    if (!editingIntegration) return;
    
    setSavingIntegration(true);
    try {
      const updateData = {
        account_name: editIntegrationForm.account_name,
        is_active: editIntegrationForm.is_active,
      };
      
      // Only include credentials if any field is filled
      const hasCredentials = Object.values(editIntegrationForm.credentials).some(v => v && v.trim());
      if (hasCredentials) {
        updateData.credentials = editIntegrationForm.credentials;
      }
      
      await advertisersAPI.updateIntegration(id, editingIntegration.id, updateData);
      toast.success('Integration updated');
      setEditIntegrationDialogOpen(false);
      setEditingIntegration(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update integration');
    } finally {
      setSavingIntegration(false);
    }
  };

  // Ad Accounts functions for Meta integrations
  const handleOpenAdAccounts = async (integration) => {
    if (integration.platform !== 'Meta') {
      toast.info('Ad account selection is only available for Meta integrations');
      return;
    }
    
    setAdAccountsIntegration(integration);
    setAdAccountsDialogOpen(true);
    setAdAccountsLoading(true);
    
    try {
      const response = await api.get(`/advertisers/${id}/integrations/${integration.id}/ad-accounts`);
      setAdAccounts(response.data.ad_accounts || []);
      const selectedAccounts = response.data.ad_accounts
        .filter(acc => acc.selected)
        .map(acc => acc.id);
      setSelectedAdAccounts(selectedAccounts);
      // Lock if an ad account has already been selected and saved
      setAdAccountLocked(response.data.is_locked || false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch ad accounts');
      setAdAccountsDialogOpen(false);
    } finally {
      setAdAccountsLoading(false);
    }
  };

  const handleToggleAdAccount = (accountId) => {
    // For Meta, only allow single selection (radio button behavior)
    // If locked, don't allow changes
    if (adAccountLocked) return;
    
    setSelectedAdAccounts([accountId]); // Single selection only
  };

  // Remove Select All / Deselect All for Meta since we only allow single selection
  const handleSelectAllAdAccounts = () => {
    // Disabled for Meta - single selection only
  };

  const handleDeselectAllAdAccounts = () => {
    if (adAccountLocked) return;
    setSelectedAdAccounts([]);
  };

  const handleSaveAdAccounts = async () => {
    if (!adAccountsIntegration) return;
    
    // Require at least one account to be selected
    if (selectedAdAccounts.length === 0) {
      toast.error('Please select at least one ad account to sync');
      return;
    }
    
    setSavingAdAccounts(true);
    try {
      await api.put(`/advertisers/${id}/integrations/${adAccountsIntegration.id}/ad-accounts`, {
        ad_accounts: selectedAdAccounts
      });
      toast.success(`${selectedAdAccounts.length} ad account(s) selected for sync`);
      setAdAccountsDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save ad accounts');
    } finally {
      setSavingAdAccounts(false);
    }
  };

  const handleDeleteAllData = async () => {
    if (!window.confirm('⚠️ WARNING: This will permanently delete ALL campaigns and performance data for this advertiser. This action cannot be undone.\n\nAre you sure you want to continue?')) {
      return;
    }
    
    // Double confirm
    if (!window.confirm('This is your final warning. All historical data will be lost.\n\nType DELETE to confirm (click OK if you understand).')) {
      return;
    }
    
    try {
      const response = await api.delete(`/advertisers/${id}/data`);
      toast.success(`Deleted ${response.data.deleted_campaigns} campaigns and ${response.data.deleted_platform_data} performance records`);
      fetchData();
      fetchAnalytics();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete data');
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.email || !newUser.name || !newUser.password) {
      toast.error('All fields are required');
      return;
    }

    setCreatingUser(true);
    try {
      await api.post(`/advertisers/${id}/users`, newUser);
      toast.success('User created');
      setUserDialogOpen(false);
      setNewUser({ email: '', name: '', password: '', role: 'user', allowed_platforms: null });
      setUserSelectAllPlatforms(true);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setCreatingUser(false);
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setEditUserForm({
      name: user.name || '',
      role: user.role || 'user',
      allowed_platforms: user.allowed_platforms || null,
    });
    setEditUserSelectAllPlatforms(!user.allowed_platforms || user.allowed_platforms.length === 0);
    setEditUserDialogOpen(true);
  };

  const handleSaveUser = async () => {
    if (!editingUser) return;
    
    setSavingUser(true);
    try {
      await api.put(`/advertisers/${id}/users/${editingUser.id}`, editUserForm);
      toast.success('User updated');
      setEditUserDialogOpen(false);
      setEditingUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setSavingUser(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Delete this user?')) {
      return;
    }

    try {
      await api.delete(`/advertisers/${id}/users/${userId}`);
      toast.success('User deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  if (!advertiser) {
    return null;
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          {isAdminRole ? (
            <Button variant="ghost" size="sm" onClick={() => navigate('/advertisers')}>
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
          ) : (
            <div className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-slate-600" />
              <span className="text-lg font-semibold text-slate-700">My Account</span>
            </div>
          )}
        </div>

        {/* Advertiser Info Card */}
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                {/* Logo with edit capability for advertisers */}
                <div className="relative">
                  {advertiser.logo_url ? (
                    <img
                      src={advertiser.logo_url}
                      alt={advertiser.name}
                      className="w-16 h-16 rounded-xl object-cover border border-slate-200"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                      <span className="text-white font-bold text-2xl">
                        {advertiser.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                  {/* Logo edit button for advertisers - always visible */}
                  {isAdvertiserRole && !editing && (
                    <button
                      onClick={() => {
                        setNewLogoUrl(advertiser.logo_url || '');
                        setLogoEditing(true);
                      }}
                      className="absolute -bottom-1 -right-1 w-6 h-6 flex items-center justify-center bg-blue-600 hover:bg-blue-700 rounded-full shadow-md transition-colors cursor-pointer"
                      data-testid="edit-logo-btn"
                      title="Edit logo"
                    >
                      <Camera className="w-3 h-3 text-white" />
                    </button>
                  )}
                </div>
                <div>
                  {editing ? (
                    <Input
                      value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      className="text-xl font-bold mb-1"
                    />
                  ) : (
                    <h1 className="text-2xl font-bold text-slate-900">{advertiser.name}</h1>
                  )}
                  <div className="flex items-center gap-4 text-sm text-slate-500">
                    <span className="flex items-center gap-1">
                      <Plug className="w-4 h-4" />
                      {advertiser.integration_count} Integrations
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      {users.length} Users
                    </span>
                    <span className="flex items-center gap-1 bg-slate-100 px-2 py-0.5 rounded font-mono text-xs">
                      <DollarSign className="w-3 h-3" />
                      {advertiser.currency || 'USD'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {/* Only admins can edit full advertiser info */}
                {isAdminRole && (
                  editing ? (
                    <>
                      <Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
                      <Button onClick={handleSave} disabled={saving}>
                        {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        <Save className="w-4 h-4 mr-1" />
                        Save
                      </Button>
                    </>
                  ) : (
                    <Button variant="outline" onClick={() => setEditing(true)}>
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                  )
                )}
              </div>
            </div>

            {/* Logo Edit Dialog for Advertisers */}
            {logoEditing && (
              <div className="mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex items-center gap-2 mb-3">
                  <ImageIcon className="w-4 h-4 text-slate-600" />
                  <Label className="text-sm font-medium">Update Company Logo</Label>
                </div>
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter logo URL (e.g., https://example.com/logo.png)"
                    value={newLogoUrl}
                    onChange={(e) => setNewLogoUrl(e.target.value)}
                    className="flex-1"
                    data-testid="logo-url-input"
                  />
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setLogoEditing(false);
                      setNewLogoUrl('');
                    }}
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleSaveLogo} 
                    disabled={saving}
                    data-testid="save-logo-btn"
                  >
                    {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Save Logo
                  </Button>
                </div>
                {newLogoUrl && (
                  <div className="mt-3">
                    <Label className="text-xs text-slate-500 mb-1 block">Preview:</Label>
                    <img 
                      src={newLogoUrl} 
                      alt="Logo preview" 
                      className="w-12 h-12 rounded object-cover border border-slate-200"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </div>
            )}

            {editing && (
              <div className="mt-6 grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Contact Name</Label>
                  <Input
                    value={editForm.contact_name || ''}
                    onChange={(e) => setEditForm({ ...editForm, contact_name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Contact Email</Label>
                  <Input
                    value={editForm.contact_email || ''}
                    onChange={(e) => setEditForm({ ...editForm, contact_email: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Logo URL</Label>
                  <Input
                    value={editForm.logo_url || ''}
                    onChange={(e) => setEditForm({ ...editForm, logo_url: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={editForm.notes || ''}
                    onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="analytics">
          <TabsList>
            <TabsTrigger value="analytics" className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="alerts" className="flex items-center gap-2">
              <Bell className="w-4 h-4" />
              Alerts
            </TabsTrigger>
            {/* Only show Platform Integrations and Users for advertiser role, not user role */}
            {currentUser?.role !== 'user' && (
              <>
                <TabsTrigger value="integrations" className="flex items-center gap-2">
                  <Plug className="w-4 h-4" />
                  Platform Integrations
                </TabsTrigger>
                {/* Show Users tab for admins and advertisers (not for 'user' role) */}
                {(currentUser?.role === 'super_admin' || currentUser?.role === 'admin' || currentUser?.role === 'advertiser') && (
                  <TabsTrigger value="users" className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Users
                  </TabsTrigger>
                )}
              </>
            )}
          </TabsList>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="mt-6">
            <div className="space-y-6">
              {/* Platform Filter */}
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-900">Analytics Overview</h2>
                <Select value={selectedPlatform} onValueChange={setSelectedPlatform}>
                  <SelectTrigger className="w-[180px]" data-testid="analytics-platform-filter">
                    <SelectValue placeholder="Select Platform" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Platforms</SelectItem>
                    {analytics?.platform_analytics?.map(p => (
                      <SelectItem key={p.platform} value={p.platform}>{p.platform}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Summary Cards */}
              {(() => {
                const filteredAnalytics = selectedPlatform === 'all' 
                  ? analytics?.platform_analytics 
                  : analytics?.platform_analytics?.filter(p => p.platform === selectedPlatform);
                const totalSpend = selectedPlatform === 'all' 
                  ? analytics?.total_spend 
                  : filteredAnalytics?.reduce((sum, p) => sum + (p.spend || 0), 0);
                const totalCampaigns = filteredAnalytics?.reduce((sum, p) => sum + (p.campaign_count || 0), 0) || 0;
                const activePlatforms = selectedPlatform === 'all' 
                  ? filteredAnalytics?.filter(p => p.spend > 0).length 
                  : (filteredAnalytics?.length > 0 ? 1 : 0);
                
                return (
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <Card className="border-slate-200">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs font-semibold text-slate-500 uppercase">Total Spend</p>
                            <p className="text-2xl font-mono font-medium text-slate-900 mt-1">
                              {analyticsLoading ? '—' : formatCurrency(totalSpend || 0, advertiser?.currency || 'USD')}
                            </p>
                          </div>
                          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                            <DollarSign className="w-5 h-5 text-green-600" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card className="border-slate-200">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs font-semibold text-slate-500 uppercase">Active Platforms</p>
                            <p className="text-2xl font-mono font-medium text-slate-900 mt-1">
                              {analyticsLoading ? '—' : activePlatforms || 0}
                            </p>
                          </div>
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                            <Plug className="w-5 h-5 text-blue-600" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card className="border-slate-200">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs font-semibold text-slate-500 uppercase">Total Campaigns</p>
                            <p className="text-2xl font-mono font-medium text-slate-900 mt-1">
                              {analyticsLoading ? '—' : totalCampaigns}
                            </p>
                          </div>
                          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                            <Target className="w-5 h-5 text-purple-600" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card className="border-slate-200">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs font-semibold text-slate-500 uppercase">Underutilized</p>
                            <p className="text-2xl font-mono font-medium text-slate-900 mt-1">
                              {analyticsLoading ? '—' : analytics?.underutilized_platforms?.length || 0}
                            </p>
                          </div>
                          <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                            <Zap className="w-5 h-5 text-amber-600" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                );
              })()}
              
              {/* Underutilized Platforms Alert */}
              {analytics?.underutilized_platforms?.length > 0 && (
                <Card className="border-amber-200 bg-amber-50">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-amber-900">Underutilized Platforms</h4>
                        <p className="text-sm text-amber-700 mt-1">
                          The following platforms are available but have no campaigns or spend: 
                          <span className="font-medium"> {analytics.underutilized_platforms.join(', ')}</span>
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Platform Performance Table */}
              <Card className="border-slate-200">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
                      Platform Performance
                    </CardTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={fetchAnalytics}
                      disabled={analyticsLoading}
                    >
                      <RefreshCw className={`w-4 h-4 ${analyticsLoading ? 'animate-spin' : ''}`} />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Platform</th>
                          <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Spend</th>
                          <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Share</th>
                          <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Impressions</th>
                          <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Clicks</th>
                          <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">CTR</th>
                          <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Campaigns</th>
                          <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analyticsLoading ? (
                          Array.from({ length: 4 }).map((_, i) => (
                            <tr key={i} className="border-b border-slate-100">
                              {Array.from({ length: 8 }).map((_, j) => (
                                <td key={j} className="py-3 px-4">
                                  <div className="h-4 bg-slate-200 animate-pulse rounded" />
                                </td>
                              ))}
                            </tr>
                          ))
                        ) : analytics?.platform_analytics?.length > 0 ? (
                          analytics.platform_analytics.map((platform) => (
                            <tr 
                              key={platform.platform} 
                              className={`border-b border-slate-100 ${platform.is_underutilized ? 'bg-amber-50/50' : ''}`}
                            >
                              <td className="py-3 px-4">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-slate-900">{platform.platform}</span>
                                  {platform.is_underutilized && (
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger>
                                          <Zap className="w-4 h-4 text-amber-500" />
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          <p>No spend or campaigns on this platform</p>
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  )}
                                </div>
                              </td>
                              <td className="text-right py-3 px-4 font-mono text-slate-900">
                                {formatCurrency(platform.spend, advertiser?.currency || 'USD')}
                              </td>
                              <td className="text-right py-3 px-4 text-slate-600">
                                {platform.spend_share > 0 ? `${platform.spend_share}%` : '—'}
                              </td>
                              <td className="text-right py-3 px-4 font-mono text-slate-700">
                                {formatNumber(platform.impressions)}
                              </td>
                              <td className="text-right py-3 px-4 font-mono text-slate-700">
                                {formatNumber(platform.clicks)}
                              </td>
                              <td className="text-right py-3 px-4 text-slate-700">
                                {platform.ctr > 0 ? `${platform.ctr}%` : '—'}
                              </td>
                              <td className="text-right py-3 px-4">
                                <div className="flex items-center justify-end gap-1">
                                  <span className="font-mono text-slate-900">{platform.campaign_count}</span>
                                  {platform.active_campaigns > 0 && (
                                    <span className="text-xs text-green-600">({platform.active_campaigns} active)</span>
                                  )}
                                </div>
                              </td>
                              <td className="text-center py-3 px-4">
                                {platform.spend > 0 ? (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                    <CheckCircle2 className="w-3 h-3 mr-1" />
                                    Active
                                  </span>
                                ) : platform.campaign_count > 0 ? (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                                    <Clock className="w-3 h-3 mr-1" />
                                    Ready
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                                    Unused
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={8} className="py-12 text-center text-slate-400">
                              No analytics data available
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>


          {/* Alerts Tab */}
          <TabsContent value="alerts" className="mt-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-slate-900">Performance Alerts</h2>
              <Dialog open={alertDialogOpen} onOpenChange={setAlertDialogOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="create-alert-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Alert
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[500px]">
                  <DialogHeader>
                    <DialogTitle>Create Performance Alert</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Alert Name</Label>
                      <Input
                        placeholder="e.g., Budget 80% Alert"
                        value={newAlert.name}
                        onChange={(e) => setNewAlert({ ...newAlert, name: e.target.value })}
                        data-testid="alert-name-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Campaign (Optional)</Label>
                      <Popover open={campaignDropdownOpen} onOpenChange={setCampaignDropdownOpen}>
                        <PopoverTrigger asChild>
                          <Button 
                            variant="outline" 
                            className="w-full justify-between"
                            data-testid="alert-campaign-select"
                          >
                            <span className="truncate">
                              {newAlert.campaign_id 
                                ? campaigns.find(c => c.id === newAlert.campaign_id)?.name || 'Selected Campaign'
                                : 'All campaigns'}
                            </span>
                            <ChevronDown className="w-4 h-4 ml-2 shrink-0" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[300px] p-2" align="start">
                          <div className="flex justify-between items-center pb-2 border-b mb-2">
                            <span className="text-sm font-medium">Select Campaign</span>
                            {newAlert.campaign_id && (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => {
                                  setNewAlert({ ...newAlert, campaign_id: '' });
                                  setCampaignDropdownOpen(false);
                                }} 
                                className="h-6 px-2 text-xs"
                              >
                                Clear
                              </Button>
                            )}
                          </div>
                          <div className="relative mb-2">
                            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input
                              placeholder="Search campaigns..."
                              className="pl-8 h-8"
                              value={campaignSearch}
                              onChange={(e) => setCampaignSearch(e.target.value)}
                            />
                          </div>
                          <div className="space-y-1 max-h-60 overflow-y-auto">
                            <div
                              className={`flex items-center gap-2 p-2 hover:bg-slate-50 rounded cursor-pointer ${!newAlert.campaign_id ? 'bg-slate-100' : ''}`}
                              onClick={() => {
                                setNewAlert({ ...newAlert, campaign_id: '' });
                                setCampaignDropdownOpen(false);
                              }}
                            >
                              <span className="text-sm font-medium">All campaigns</span>
                            </div>
                            {campaigns
                              .filter(c => c.name?.toLowerCase().includes(campaignSearch.toLowerCase()))
                              .slice(0, 50)
                              .map((c) => (
                              <div
                                key={c.id}
                                className={`flex items-center gap-2 p-2 hover:bg-slate-50 rounded cursor-pointer ${newAlert.campaign_id === c.id ? 'bg-slate-100' : ''}`}
                                onClick={() => {
                                  setNewAlert({ ...newAlert, campaign_id: c.id });
                                  setCampaignDropdownOpen(false);
                                  setCampaignSearch('');
                                }}
                              >
                                <div className="flex-1 min-w-0">
                                  <span className="text-sm block truncate" title={c.name}>{c.name}</span>
                                  {c.ad_account_name && (
                                    <span className="text-xs text-slate-500 block truncate">{c.ad_account_name}</span>
                                  )}
                                </div>
                              </div>
                            ))}
                            {campaigns.filter(c => c.name?.toLowerCase().includes(campaignSearch.toLowerCase())).length === 0 && (
                              <p className="text-xs text-slate-500 text-center py-4">No campaigns match your search</p>
                            )}
                            {campaigns.filter(c => c.name?.toLowerCase().includes(campaignSearch.toLowerCase())).length > 50 && (
                              <p className="text-xs text-slate-400 text-center py-2">
                                Showing 50 of {campaigns.filter(c => c.name?.toLowerCase().includes(campaignSearch.toLowerCase())).length} campaigns
                              </p>
                            )}
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>

                    <div className="space-y-2">
                      <Label>Alert Type</Label>
                      <Select
                        value={newAlert.alert_type}
                        onValueChange={(val) => setNewAlert({ ...newAlert, alert_type: val })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="threshold">Threshold Alert</SelectItem>
                          <SelectItem value="budget_exhaustion">Budget Exhaustion</SelectItem>
                          <SelectItem value="performance_drop">Performance Drop</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Metric</Label>
                        <Select
                          value={newAlert.threshold_metric}
                          onValueChange={(val) => setNewAlert({ ...newAlert, threshold_metric: val })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="spend">Spend</SelectItem>
                            <SelectItem value="impressions">Impressions</SelectItem>
                            <SelectItem value="clicks">Clicks</SelectItem>
                            <SelectItem value="ctr">CTR (%)</SelectItem>
                            <SelectItem value="budget_percent">Budget Usage (%)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Threshold Value</Label>
                        <Input
                          type="number"
                          min="0"
                          step="any"
                          placeholder="e.g., 1000 or 80%"
                          value={newAlert.threshold_value}
                          onChange={(e) => setNewAlert({ ...newAlert, threshold_value: parseFloat(e.target.value) || 0 })}
                          data-testid="alert-threshold-input"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Cooldown (hours)</Label>
                      <Input
                        type="number"
                        min="0"
                        value={newAlert.cooldown_hours}
                        onChange={(e) => setNewAlert({ ...newAlert, cooldown_hours: parseInt(e.target.value) || 0 })}
                        className="w-24"
                      />
                      <p className="text-xs text-slate-500">Minimum time between repeated alerts</p>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={() => setAlertDialogOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleCreateAlert}
                      disabled={creatingAlert}
                      data-testid="save-alert-btn"
                    >
                      {creatingAlert ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Create Alert
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            {alertsLoading ? (
              <Card className="border-slate-200">
                <CardContent className="py-12 text-center">
                  <Loader2 className="w-8 h-8 animate-spin text-slate-400 mx-auto" />
                  <p className="text-slate-500 mt-2">Loading alerts...</p>
                </CardContent>
              </Card>
            ) : alerts.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <Bell className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-700 mb-2">No alerts configured</h3>
                  <p className="text-slate-500 mb-4">Create performance alerts to get notified when your campaigns hit thresholds.</p>
                  <Button variant="outline" onClick={() => setAlertDialogOpen(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Your First Alert
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {alerts.map((alert) => (
                  <Card key={alert.id} className="border-slate-200">
                    <CardContent className="py-4 px-5">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${alert.is_active ? 'bg-green-100' : 'bg-slate-100'}`}>
                            <Bell className={`w-5 h-5 ${alert.is_active ? 'text-green-600' : 'text-slate-400'}`} />
                          </div>
                          <div>
                            <h4 className="font-medium text-slate-900">{alert.name}</h4>
                            <p className="text-sm text-slate-500">
                              {alert.alert_type === 'threshold' && 'Threshold: '}
                              {alert.alert_type === 'budget_exhaustion' && 'Budget: '}
                              {alert.alert_type === 'performance_drop' && 'Performance: '}
                              <span className="font-mono">
                                {alert.threshold_metric === 'budget_percent' || alert.threshold_metric === 'ctr'
                                  ? `${alert.threshold_value}%`
                                  : `${alert.threshold_value?.toLocaleString()}`}
                              </span>
                              {' '}({alert.threshold_metric})
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            alert.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'
                          }`}>
                            {alert.is_active ? 'Active' : 'Paused'}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleAlert(alert)}
                            data-testid={`toggle-alert-${alert.id}`}
                          >
                            {alert.is_active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                            onClick={() => handleDeleteAlert(alert.id)}
                            data-testid={`delete-alert-${alert.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Integrations Tab */}
          <TabsContent value="integrations" className="mt-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-slate-900">Platform Integrations</h2>
              <div className="flex items-center gap-2">
                {(isAdminRole || isAdvertiserRole) && (
                  <Button 
                    variant="outline" 
                    onClick={handleDeleteAllData}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid="delete-all-data-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete All Data
                  </Button>
                )}
                <Dialog open={integrationDialogOpen} onOpenChange={setIntegrationDialogOpen}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-integration-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Integration
                    </Button>
                  </DialogTrigger>
                <DialogContent className="sm:max-w-[500px]">
                  <DialogHeader>
                    <DialogTitle>Add Platform Integration</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Platform *</Label>
                      <Select
                        value={newIntegration.platform}
                        onValueChange={(value) => setNewIntegration({
                          ...newIntegration,
                          platform: value,
                          credentials: {},
                        })}
                      >
                        <SelectTrigger data-testid="platform-select">
                          <SelectValue placeholder="Select platform" />
                        </SelectTrigger>
                        <SelectContent>
                          {PLATFORMS
                            .filter((p) => {
                              // If advertiser has allowed_platforms set, only show those
                              if (advertiser?.allowed_platforms && advertiser.allowed_platforms.length > 0) {
                                return advertiser.allowed_platforms.includes(p.value);
                              }
                              // If no restriction, show all platforms
                              return true;
                            })
                            .map((p) => (
                            <SelectItem key={p.value} value={p.value}>
                              <span className="flex items-center gap-2">
                                <span>{p.icon}</span>
                                <span>{p.label}</span>
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Account Name *</Label>
                      <Input
                        placeholder="e.g., Main Account, Client A"
                        value={newIntegration.account_name}
                        onChange={(e) => setNewIntegration({ ...newIntegration, account_name: e.target.value })}
                        data-testid="account-name-input"
                      />
                      <p className="text-xs text-slate-500">A friendly name to identify this account</p>
                    </div>

                    {newIntegration.platform && (
                      <div className="space-y-4 pt-4 border-t">
                        <h4 className="font-medium text-slate-700">Credentials</h4>
                        {getCredentialFields(newIntegration.platform).map((field) => (
                          <div key={field.key} className="space-y-2">
                            <Label>
                              {field.label} {field.required && '*'}
                            </Label>
                            <div className="relative">
                              <Input
                                type={showPasswords[field.key] ? 'text' : field.type}
                                placeholder={`Enter ${field.label.toLowerCase()}`}
                                value={newIntegration.credentials[field.key] || ''}
                                onChange={(e) => setNewIntegration({
                                  ...newIntegration,
                                  credentials: {
                                    ...newIntegration.credentials,
                                    [field.key]: e.target.value,
                                  },
                                })}
                              />
                              {field.type === 'password' && (
                                <button
                                  type="button"
                                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                  onClick={() => setShowPasswords({
                                    ...showPasswords,
                                    [field.key]: !showPasswords[field.key],
                                  })}
                                >
                                  {showPasswords[field.key] ? (
                                    <EyeOff className="w-4 h-4" />
                                  ) : (
                                    <Eye className="w-4 h-4" />
                                  )}
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Assign to User (Optional) */}
                    <div className="space-y-2 pt-4 border-t">
                      <Label>Assign to User (Optional)</Label>
                      <Select
                        value={newIntegration.user_id || 'all'}
                        onValueChange={(value) => setNewIntegration({
                          ...newIntegration,
                          user_id: value === 'all' ? '' : value,
                        })}
                      >
                        <SelectTrigger data-testid="user-select">
                          <SelectValue placeholder="All users (shared)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">
                            <span className="flex items-center gap-2">
                              <Users className="w-4 h-4" />
                              All users (shared integration)
                            </span>
                          </SelectItem>
                          {users.map((user) => (
                            <SelectItem key={user.id} value={user.id}>
                              <span className="flex items-center gap-2">
                                <User className="w-4 h-4" />
                                {user.name} ({user.email})
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-slate-500">
                        If assigned to a specific user, only that user will see data from this integration
                      </p>
                    </div>

                    <Button
                      onClick={handleCreateIntegration}
                      disabled={creatingIntegration}
                      className="w-full"
                      data-testid="submit-integration-btn"
                    >
                      {creatingIntegration && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Add Integration
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
              </div>
            </div>

            {integrations.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <Plug className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-700 mb-2">No integrations yet</h3>
                  <p className="text-slate-500 mb-4">Add platform integrations to start syncing campaign data</p>
                  <Button onClick={() => setIntegrationDialogOpen(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Integration
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {integrations.map((integration) => {
                  const platform = PLATFORMS.find((p) => p.value === integration.platform);
                  const isConnected = integration.connection_status === 'connected';
                  const isError = integration.connection_status === 'error';

                  return (
                    <Card key={integration.id} className="border-slate-200">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">{platform?.icon || '📊'}</span>
                            <div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <h4 className="font-medium text-slate-900">{integration.account_name}</h4>
                                <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                                  {integration.platform}
                                </span>
                                {integration.user_name ? (
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 flex items-center gap-1">
                                    <User className="w-3 h-3" />
                                    {integration.user_name}
                                  </span>
                                ) : (
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 flex items-center gap-1">
                                    <Users className="w-3 h-3" />
                                    Shared
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-3 mt-1 text-sm">
                                <span className={`flex items-center gap-1 ${
                                  isConnected ? 'text-green-600' : isError ? 'text-red-600' : 'text-amber-600'
                                }`}>
                                  {isConnected ? (
                                    <CheckCircle2 className="w-4 h-4" />
                                  ) : isError ? (
                                    <AlertCircle className="w-4 h-4" />
                                  ) : integration.connection_status === 'syncing' ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Clock className="w-4 h-4" />
                                  )}
                                  {integration.connection_status}
                                  {integration.sync_progress && (
                                    <span className="text-blue-600 ml-1">({integration.sync_progress})</span>
                                  )}
                                </span>
                                {integration.last_sync && (
                                  <span className="text-slate-500">
                                    Last sync: {new Date(integration.last_sync).toLocaleString()}
                                  </span>
                                )}
                                {integration.last_sync_performance > 0 && (
                                  <span className="text-slate-400">
                                    {integration.last_sync_performance.toLocaleString()} records
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            {integration.platform === 'Meta' && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleOpenAdAccounts(integration)}
                                data-testid={`ad-accounts-${integration.id}`}
                                className="text-blue-600 hover:text-blue-700"
                              >
                                <Building2 className="w-4 h-4 mr-1" />
                                Ad Accounts
                              </Button>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleTestIntegration(integration.id)}
                              data-testid={`test-integration-${integration.id}`}
                            >
                              <Play className="w-4 h-4 mr-1" />
                              Test
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleSyncIntegration(integration.id)}
                              disabled={!isConnected}
                              data-testid={`sync-integration-${integration.id}`}
                            >
                              <RefreshCw className="w-4 h-4 mr-1" />
                              Sync
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditIntegration(integration)}
                              data-testid={`edit-integration-${integration.id}`}
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              Edit
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteIntegration(integration.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-integration-${integration.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
            
            {/* Edit Integration Dialog */}
            <Dialog open={editIntegrationDialogOpen} onOpenChange={setEditIntegrationDialogOpen}>
              <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Edit Integration</DialogTitle>
                </DialogHeader>
                {editingIntegration && (
                  <div className="space-y-4 py-4">
                    <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                      <span className="text-2xl">
                        {PLATFORMS.find(p => p.value === editingIntegration.platform)?.icon || '📊'}
                      </span>
                      <div>
                        <p className="font-medium">{editingIntegration.platform}</p>
                        <p className="text-xs text-slate-500">Platform cannot be changed</p>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Account Name *</Label>
                      <Input
                        placeholder="My Account"
                        value={editIntegrationForm.account_name}
                        onChange={(e) => setEditIntegrationForm({
                          ...editIntegrationForm,
                          account_name: e.target.value,
                        })}
                        data-testid="edit-account-name-input"
                      />
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="edit-is-active"
                        checked={editIntegrationForm.is_active}
                        onCheckedChange={(checked) => setEditIntegrationForm({
                          ...editIntegrationForm,
                          is_active: checked,
                        })}
                      />
                      <Label htmlFor="edit-is-active">Active</Label>
                    </div>

                    {/* Credential Fields */}
                    <div className="space-y-3 pt-2 border-t">
                      <Label className="text-sm font-medium">Update Credentials (leave blank to keep existing)</Label>
                      {getCredentialFields(editingIntegration.platform).map((field) => (
                        <div key={field.key} className="space-y-1">
                          <Label className="text-sm">{field.label}</Label>
                          <div className="relative">
                            <Input
                              type={field.type === 'password' && !editShowPasswords[field.key] ? 'password' : 'text'}
                              placeholder={`Enter new ${field.label.toLowerCase()}`}
                              value={editIntegrationForm.credentials[field.key] || ''}
                              onChange={(e) => setEditIntegrationForm({
                                ...editIntegrationForm,
                                credentials: {
                                  ...editIntegrationForm.credentials,
                                  [field.key]: e.target.value,
                                },
                              })}
                              data-testid={`edit-credential-${field.key}`}
                            />
                            {field.type === 'password' && (
                              <button
                                type="button"
                                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                onClick={() => setEditShowPasswords({
                                  ...editShowPasswords,
                                  [field.key]: !editShowPasswords[field.key],
                                })}
                              >
                                {editShowPasswords[field.key] ? (
                                  <EyeOff className="w-4 h-4" />
                                ) : (
                                  <Eye className="w-4 h-4" />
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    <DialogFooter>
                      <Button
                        variant="outline"
                        onClick={() => setEditIntegrationDialogOpen(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleSaveIntegration}
                        disabled={savingIntegration || !editIntegrationForm.account_name}
                        data-testid="save-integration-btn"
                      >
                        {savingIntegration && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        Save Changes
                      </Button>
                    </DialogFooter>
                  </div>
                )}
              </DialogContent>
            </Dialog>
            
            {/* Ad Accounts Selection Dialog (Meta Only) */}
            <Dialog open={adAccountsDialogOpen} onOpenChange={setAdAccountsDialogOpen}>
              <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-hidden flex flex-col">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Building2 className="w-5 h-5" />
                    Select Ad Account to Sync
                  </DialogTitle>
                  <p className="text-sm text-slate-500">
                    {adAccountLocked 
                      ? 'Ad account selection is locked. Contact support to change the ad account.'
                      : 'Choose ONE Meta ad account to sync. This selection cannot be changed after saving.'}
                  </p>
                </DialogHeader>
                
                {adAccountsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                    <span className="ml-2 text-slate-500">Loading ad accounts...</span>
                  </div>
                ) : (
                  <>
                    {adAccountLocked && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800 flex items-center gap-2">
                        <Lock className="w-4 h-4" />
                        <span>Ad account selection is locked and cannot be changed.</span>
                      </div>
                    )}
                    
                    <div className="flex items-center justify-between py-2 border-b">
                      <span className="text-sm text-slate-600">
                        {selectedAdAccounts.length === 1 ? '1 account selected' : 'Select one account'}
                      </span>
                      {!adAccountLocked && selectedAdAccounts.length > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleDeselectAllAdAccounts}
                        >
                          Clear Selection
                        </Button>
                      )}
                    </div>
                    
                    <div className="flex-1 overflow-y-auto max-h-[400px] space-y-2 py-2">
                      {adAccounts.length === 0 ? (
                        <p className="text-center text-slate-500 py-8">No ad accounts found</p>
                      ) : (
                        adAccounts.map((account) => {
                          const isActive = account.status === 1;
                          const isSelected = selectedAdAccounts.includes(account.id);
                          const isDisabled = !isActive || (adAccountLocked && !isSelected);
                          
                          return (
                            <div
                              key={account.id}
                              onClick={() => !isDisabled && !adAccountLocked && handleToggleAdAccount(account.id)}
                              className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${
                                isSelected ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200' : 'border-slate-200 hover:border-slate-300'
                              } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''} ${adAccountLocked && !isSelected ? 'hidden' : ''}`}
                            >
                              <div className="flex items-center gap-3">
                                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                  isSelected ? 'border-blue-500 bg-blue-500' : 'border-slate-300'
                                }`}>
                                  {isSelected && <div className="w-2 h-2 bg-white rounded-full" />}
                                </div>
                                <div>
                                  <p className="font-medium text-slate-900">{account.name}</p>
                                  <div className="flex items-center gap-2 text-xs text-slate-500">
                                    <span>ID: {account.id}</span>
                                    <span>•</span>
                                    <span>{account.currency}</span>
                                    {account.business_name && (
                                      <>
                                        <span>•</span>
                                        <span>{account.business_name}</span>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <span className={`text-xs px-2 py-1 rounded ${
                                isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                              }`}>
                                {account.status_text}
                              </span>
                            </div>
                          );
                        })
                      )}
                    </div>
                    
                    <DialogFooter className="border-t pt-4">
                      <Button
                        variant="outline"
                        onClick={() => setAdAccountsDialogOpen(false)}
                      >
                        {adAccountLocked ? 'Close' : 'Cancel'}
                      </Button>
                      {!adAccountLocked && (
                        <Button
                          onClick={handleSaveAdAccounts}
                          disabled={savingAdAccounts || selectedAdAccounts.length !== 1}
                          data-testid="save-ad-accounts-btn"
                        >
                          {savingAdAccounts && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                          Save & Lock Selection
                        </Button>
                      )}
                    </DialogFooter>
                  </>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="mt-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-slate-900">Advertiser Users</h2>
              <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="add-user-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add User
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Create Advertiser User</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <p className="text-sm text-slate-500">
                      This user will be able to login and view reports for this advertiser only.
                    </p>
                    <div className="space-y-2">
                      <Label>Name *</Label>
                      <Input
                        placeholder="John Doe"
                        value={newUser.name}
                        onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                        data-testid="user-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        placeholder="john@example.com"
                        value={newUser.email}
                        onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                        data-testid="user-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Password *</Label>
                      <Input
                        type="password"
                        placeholder="••••••••"
                        value={newUser.password}
                        onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                        data-testid="user-password-input"
                      />
                    </div>
                    
                    {/* Role Selection */}
                    <div className="space-y-2">
                      <Label>Role *</Label>
                      <Select 
                        value={newUser.role} 
                        onValueChange={(value) => setNewUser({ ...newUser, role: value })}
                      >
                        <SelectTrigger data-testid="user-role-select">
                          <SelectValue placeholder="Select role" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="user">User (Limited Access)</SelectItem>
                          <SelectItem value="advertiser">Advertiser (Full Access)</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-slate-500">
                        Users have limited access. Advertisers can manage integrations and other users.
                      </p>
                    </div>
                    
                    {/* Platform Access Control for User */}
                    <div className="space-y-3">
                      <Label>Allowed Platforms</Label>
                      <p className="text-xs text-slate-500">
                        Restrict which platforms this user can access. 
                        {advertiser?.allowed_platforms ? ` (Advertiser is limited to: ${advertiser.allowed_platforms.join(', ')})` : ''}
                      </p>
                      <div className="flex items-center gap-2 mb-2">
                        <Checkbox
                          id="user-select-all-platforms"
                          checked={userSelectAllPlatforms}
                          onCheckedChange={(checked) => {
                            setUserSelectAllPlatforms(checked);
                            if (checked) {
                              setNewUser({ ...newUser, allowed_platforms: null });
                            } else {
                              setNewUser({ ...newUser, allowed_platforms: [] });
                            }
                          }}
                          data-testid="user-select-all-platforms"
                        />
                        <Label htmlFor="user-select-all-platforms" className="text-sm font-medium cursor-pointer">
                          Inherit from Advertiser (All available platforms)
                        </Label>
                      </div>
                      {!userSelectAllPlatforms && (
                        <div className="grid grid-cols-2 gap-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
                          {PLATFORMS.filter(p => 
                            !advertiser?.allowed_platforms || advertiser.allowed_platforms.includes(p.value)
                          ).map((platform) => (
                            <div key={platform.value} className="flex items-center gap-2">
                              <Checkbox
                                id={`user-platform-${platform.value}`}
                                checked={newUser.allowed_platforms?.includes(platform.value) || false}
                                onCheckedChange={(checked) => {
                                  const current = newUser.allowed_platforms || [];
                                  if (checked) {
                                    setNewUser({
                                      ...newUser,
                                      allowed_platforms: [...current, platform.value]
                                    });
                                  } else {
                                    setNewUser({
                                      ...newUser,
                                      allowed_platforms: current.filter(p => p !== platform.value)
                                    });
                                  }
                                }}
                                data-testid={`user-platform-checkbox-${platform.value}`}
                              />
                              <Label 
                                htmlFor={`user-platform-${platform.value}`} 
                                className="text-sm cursor-pointer flex items-center gap-1"
                              >
                                <span>{platform.icon}</span>
                                <span>{platform.value}</span>
                              </Label>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    <Button
                      onClick={handleCreateUser}
                      disabled={creatingUser}
                      className="w-full"
                      data-testid="submit-user-btn"
                    >
                      {creatingUser && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Create User
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {users.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-700 mb-2">No users yet</h3>
                  <p className="text-slate-500 mb-4">Create users so the advertiser can access their dashboard</p>
                  <Button onClick={() => setUserDialogOpen(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add User
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {users.map((user) => (
                  <Card key={user.id} className="border-slate-200">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                            <User className="w-5 h-5 text-slate-500" />
                          </div>
                          <div>
                            <h4 className="font-medium text-slate-900">{user.name}</h4>
                            <p className="text-sm text-slate-500">{user.email}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                user.role === 'advertiser' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                              }`}>
                                {user.role === 'advertiser' ? 'Advertiser' : 'User'}
                              </span>
                              {user.allowed_platforms && (
                                <span className="text-xs text-slate-500">
                                  Platforms: {user.allowed_platforms.join(', ')}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            user.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                          }`}>
                            {user.is_active ? 'Active' : 'Inactive'}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditUser(user)}
                            data-testid={`edit-user-${user.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteUser(user.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-user-${user.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
            
            {/* Edit User Dialog */}
            <Dialog open={editUserDialogOpen} onOpenChange={setEditUserDialogOpen}>
              <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                  <DialogTitle>Edit User</DialogTitle>
                </DialogHeader>
                {editingUser && (
                  <div className="space-y-4 py-4">
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-sm text-slate-500">Email</p>
                      <p className="font-medium">{editingUser.email}</p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Name</Label>
                      <Input
                        placeholder="John Doe"
                        value={editUserForm.name}
                        onChange={(e) => setEditUserForm({ ...editUserForm, name: e.target.value })}
                        data-testid="edit-user-name-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Role</Label>
                      <Select 
                        value={editUserForm.role} 
                        onValueChange={(value) => setEditUserForm({ ...editUserForm, role: value })}
                      >
                        <SelectTrigger data-testid="edit-user-role-select">
                          <SelectValue placeholder="Select role" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="user">User (Limited Access)</SelectItem>
                          <SelectItem value="advertiser">Advertiser (Full Access)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Platform Access Control */}
                    <div className="space-y-3">
                      <Label>Allowed Platforms</Label>
                      <div className="flex items-center gap-2 mb-2">
                        <Checkbox
                          id="edit-user-select-all-platforms"
                          checked={editUserSelectAllPlatforms}
                          onCheckedChange={(checked) => {
                            setEditUserSelectAllPlatforms(checked);
                            if (checked) {
                              setEditUserForm({ ...editUserForm, allowed_platforms: null });
                            } else {
                              setEditUserForm({ ...editUserForm, allowed_platforms: [] });
                            }
                          }}
                        />
                        <Label htmlFor="edit-user-select-all-platforms" className="text-sm cursor-pointer">
                          All Platforms
                        </Label>
                      </div>
                      {!editUserSelectAllPlatforms && (
                        <div className="grid grid-cols-2 gap-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
                          {PLATFORMS.filter(p => 
                            !advertiser?.allowed_platforms || advertiser.allowed_platforms.includes(p.value)
                          ).map((platform) => (
                            <div key={platform.value} className="flex items-center gap-2">
                              <Checkbox
                                id={`edit-user-platform-${platform.value}`}
                                checked={editUserForm.allowed_platforms?.includes(platform.value) || false}
                                onCheckedChange={(checked) => {
                                  const current = editUserForm.allowed_platforms || [];
                                  if (checked) {
                                    setEditUserForm({
                                      ...editUserForm,
                                      allowed_platforms: [...current, platform.value]
                                    });
                                  } else {
                                    setEditUserForm({
                                      ...editUserForm,
                                      allowed_platforms: current.filter(p => p !== platform.value)
                                    });
                                  }
                                }}
                              />
                              <Label 
                                htmlFor={`edit-user-platform-${platform.value}`} 
                                className="text-sm cursor-pointer flex items-center gap-1"
                              >
                                <span>{platform.icon}</span>
                                <span>{platform.value}</span>
                              </Label>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setEditUserDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button onClick={handleSaveUser} disabled={savingUser} data-testid="save-user-btn">
                        {savingUser && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        Save Changes
                      </Button>
                    </DialogFooter>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default AdvertiserDetail;
